/**
 * Una seccion del home como renglon de lista, para ordenarla y nombrarla.
 *
 * POR QUE CON LOS PRIMITIVOS DEL SISTEMA Y NO CON LOS DE LA TIENDA
 * ----------------------------------------------------------------
 * Vivia en `tienda/vidriera.tsx`, pintada en blanco y negro como la tienda. Pero
 * esto no es una vista previa: es una lista de administracion, como la de las
 * ofertas. En modo oscuro quedaba una franja blanca en el medio de la pantalla.
 * Ahora es igual a `FilaOferta`: asa, titulo, detalle, "Se ve" y un menu.
 *
 * SE DESPLIEGA Y ADENTRO ESTAN SUS PRODUCTOS
 * ------------------------------------------
 * Antes la lista servia para ordenar y nombrar, y cada seccion volvia a
 * aparecer mas abajo, entera, para elegir sus productos: la misma cosa en dos
 * lugares. Ahora el renglon se despliega (como un rubro en Que se publica) y
 * lo de adentro lo pone quien la usa (`children`).
 *
 * EL TITULO SE ESCRIBE AHI MISMO
 * ------------------------------
 * Se guarda al salir del campo o con Enter. En una automatica, el titulo vacio
 * vuelve al del rubro. Una propia no tiene "de siempre": su nombre es el que se
 * le puso, y vacio no se guarda.
 *
 * Borrar va en el menu, nunca suelto (MASTER §6), y solo en las propias: las
 * automaticas salen de los rubros y se ocultan, no se borran.
 */
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight, GripVertical, MoreHorizontal, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export function FilaSeccion({
  clave,
  titulo,
  deSiempre,
  detalle,
  propia,
  visible,
  abierta,
  onAbrir,
  children,
  onTitulo,
  onVisible,
  onBorrar,
}: {
  clave: string;
  titulo: string;
  deSiempre: string;
  detalle: string;
  propia: boolean;
  visible: boolean;
  abierta: boolean;
  onAbrir: (abierta: boolean) => void;
  /** Lo que se ve desplegada: los productos de la seccion. */
  children: ReactNode;
  onTitulo: (titulo: string) => void;
  onVisible: (visible: boolean) => void;
  onBorrar: () => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: clave,
  });
  const [texto, setTexto] = useState(propia || titulo !== deSiempre ? titulo : "");

  function alSalir(): void {
    const limpio = texto.trim();
    if (propia) {
      if (limpio === "") setTexto(titulo);
      else if (limpio !== titulo) onTitulo(limpio);
      return;
    }
    if ((limpio || deSiempre) !== titulo) onTitulo(limpio);
  }

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "border-b border-border bg-card last:border-b-0",
        isDragging && "relative z-10 shadow-md",
      )}
    >
      <div className="flex min-h-12 items-center gap-1 px-1">
        <IconButton
          icono={GripVertical}
          tamano="sm"
          aria-label={`Mover la sección ${titulo}`}
          className="shrink-0 cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
          {...attributes}
          {...listeners}
        />
        <IconButton
          icono={abierta ? ChevronDown : ChevronRight}
          tamano="sm"
          aria-label={abierta ? `Cerrar ${titulo}` : `Ver los productos de ${titulo}`}
          aria-expanded={abierta}
          onClick={() => onAbrir(!abierta)}
          className="shrink-0 text-muted-foreground"
        />

        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2">
          {/* Sin borde hasta que se lo toca: se lee como un titulo, se edita
            como un campo. */}
          <Input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onBlur={alSalir}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            placeholder={propia ? "Nombre de la sección" : deSiempre}
            maxLength={40}
            aria-label={`Título de la sección ${titulo}`}
            className={cn(
              "h-9 min-w-0 flex-1 basis-full border-transparent bg-transparent px-2 font-medium placeholder:text-foreground hover:border-input focus-visible:placeholder:text-muted-foreground sm:h-9 sm:basis-40",
              !visible && "text-muted-foreground line-through placeholder:text-muted-foreground",
            )}
          />
          {/* El detalle tambien despliega: es lo que se mira para decidir abrirla. */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            tabIndex={-1}
            onClick={() => onAbrir(!abierta)}
            className="shrink-0 px-2 font-normal text-muted-foreground"
          >
            {propia ? <Badge variant="outline">Armada por vos</Badge> : null}
            {detalle}
          </Button>
        </div>

        <Checkbox
          label="Se ve"
          checked={visible}
          onChange={(e) => onVisible(e.target.checked)}
          className="shrink-0 px-2"
        />

        {propia ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                icono={MoreHorizontal}
                tamano="sm"
                aria-label={`Más acciones de ${titulo}`}
                className="shrink-0"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={onBorrar}
              >
                <Trash2 className="size-4" aria-hidden="true" />
                Borrar la sección
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          // El mismo ancho que el menu, para que los "Se ve" queden en fila.
          <span className="size-8 shrink-0" aria-hidden="true" />
        )}
      </div>

      {abierta ? <div className="border-t border-border p-3">{children}</div> : null}
    </li>
  );
}
